import { downloadIteroAsset } from '../../../../../services/iteroBrowse';
import type { ScanImageAssetType } from './scanImageTabs';
import { downloadPercent, type ScanDownloadPhase } from './scanPreviewState';
import { extractImages, openScanMeshes, type ScanImage, type ScanMeshSet } from './scanZip';

export interface ScanArchive {
    blob: Blob;
    contentDisposition: string | null;
    contentType: string | null;
}

// The archive is kept so the Files row can save the same download instead of fetching it again.
export type ScanImagesResult =
    | { kind: 'ready'; payload: readonly ScanImage[]; archive: ScanArchive }
    | { kind: 'generating' }
    | { kind: 'empty' };

export type ScanMeshesResult =
    | { kind: 'ready'; payload: ScanMeshSet; archive: ScanArchive }
    | { kind: 'generating' }
    | { kind: 'empty' };

export async function fetchScanImages(
    practiceId: string,
    rxId: string,
    assetType: ScanImageAssetType,
    signal: AbortSignal
): Promise<ScanImagesResult> {
    const result = await downloadIteroAsset(practiceId, rxId, assetType, { signal });
    if (result.kind !== 'ready') return result;
    const { blob, contentDisposition, contentType } = result;
    return {
        kind: 'ready',
        payload: await extractImages(blob, contentType),
        archive: { blob, contentDisposition, contentType }
    };
}

interface FetchMeshesOptions {
    signal: AbortSignal;
    onPhase: (phase: ScanDownloadPhase) => void;
}

// Progress events fire per network chunk; only a new whole percent is worth a re-render.
function percentReporter(onPhase: FetchMeshesOptions['onPhase']) {
    let lastPercent: number | null = null;
    return ({ loadedBytes, totalBytes }: { loadedBytes: number; totalBytes: number | null }) => {
        const percent = downloadPercent(loadedBytes, totalBytes);
        if (percent === lastPercent) return;
        lastPercent = percent;
        onPhase({ phase: 'downloading', percent });
    };
}

export async function fetchScanMeshes(
    practiceId: string,
    rxId: string,
    { signal, onPhase }: FetchMeshesOptions
): Promise<ScanMeshesResult> {
    const result = await downloadIteroAsset(practiceId, rxId, 'IDE', {
        signal,
        onProgress: percentReporter(onPhase)
    });
    if (result.kind !== 'ready') return result;
    onPhase({ phase: 'unzipping' });
    const { blob, contentDisposition, contentType } = result;
    return {
        kind: 'ready',
        payload: await openScanMeshes(blob),
        archive: { blob, contentDisposition, contentType }
    };
}
